import React from 'react'
import { Icon, Menu } from 'react-native-paper'
import { useTheme } from '../../hooks/theme.hook'
import { TouchableIconWithToolTip } from '../../utils/meldd-tooltip'
import { useTranslation } from 'react-i18next'

export type SortField = string
export type SortDirection = 'ASC' | 'DESC'

export interface SortOption<T = string> {
  field: T
  direction: SortDirection
  label: string
  icon?: string
}

export interface SortOptionsProps<T = string> {
  options: SortOption<T>[]
  selectedField: T
  selectedDirection: SortDirection
  onSortChange: (field: T, direction: SortDirection) => void
  getIcon?: (field: T, direction: SortDirection) => string
}

export function SortOptions<T extends string = string>({
  options,
  selectedField,
  selectedDirection,
  onSortChange,
  getIcon,
}: SortOptionsProps<T>) {
  const theme = useTheme()
  const [visible, setVisible] = React.useState(false)
  const { t } = useTranslation('common')
  const openMenu = () => setVisible(true)
  const closeMenu = () => setVisible(false)

  const handleSelect = (field: T, direction: SortDirection) => {
    onSortChange(field, direction)
    closeMenu()
  }

  const selectedOption = options.find(
    (option) =>
      option.field === selectedField && option.direction === selectedDirection
  )

  const currentIcon =
    selectedOption?.icon ||
    (getIcon ? getIcon(selectedField, selectedDirection) : 'sort')

  return (
    <Menu
      visible={visible}
      onDismiss={closeMenu}
      anchor={
        <TouchableIconWithToolTip
          icon={
            <Icon
              source={currentIcon}
              size={24}
              color={theme.colors.onSurface}
            />
          }
          tooltip={selectedOption?.label || t('sort')}
          onPress={openMenu}
        />
      }
      anchorPosition="bottom"
    >
      {options.map((option) => {
        const isSelected =
          option.field === selectedField &&
          option.direction === selectedDirection
        return (
          <Menu.Item
            key={`${option.field}-${option.direction}`}
            onPress={() => handleSelect(option.field, option.direction)}
            title={option.label}
            leadingIcon={isSelected ? 'check' : undefined}
          />
        )
      })}
    </Menu>
  )
}
